<script setup lang="ts" generic="T">
import { Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { type Component, computed, inject, nextTick, onMounted, onUnmounted, provide, ref, watch } from "vue";

import DropTarget from "~/components/dumb/DropTarget.vue";
import ModalInner from "~/components/dumb/ModalInner.vue";
import Template from "~/components/dumb/Template.vue";
import MobileNavigationSwipe from "~/components/MobileNavigationSwipe.vue";
import { PARENT_ON_AFTER_CLOSE_FUNCTION, PARENT_ON_AFTER_OPEN_FUNCTION } from "~/constants/injection";
import { colorsByTheme } from "~/constants/style";
import { IconMode, ModalPosition, ModalWidth } from "~/shared/enums";
import { useAppStore, usePageStore } from "~/stores";
import { useMobileSheetSwipe } from "~/utils/swipe";
import { when } from "~/utils/wait";

const TRANSITION_RIGHT = {
  enter: "ease-in-out duration-150",
  enterFrom: "translate-x-full",
  enterTo: "translate-x-0",
  leave: "ease-in-out duration-150",
  leaveFrom: "translate-x-0",
  leaveTo: "translate-x-full",
};
const TRANSITION_LEFT = {
  enter: "ease-in-out duration-150",
  enterFrom: "-translate-x-full",
  enterTo: "translate-x-0",
  leave: "ease-in-out duration-150",
  leaveFrom: "translate-x-0",
  leaveTo: "-translate-x-full",
};
const TRANSITION_NORMAL = {
  enter: "duration-150 ease-out",
  enterFrom: "opacity-0 scale-95",
  enterTo: "opacity-100 scale-100",
  leave: "duration-150 ease-in",
  leaveFrom: "opacity-100 scale-100",
  leaveTo: "opacity-0 scale-95",
};
const TRANSITION_MOBILE_SWIPE = {
  enter: "ease-in-out duration-150",
  enterFrom: "translate-y-full",
  enterTo: "translate-y-0",
  leave: "ease-in-out duration-150",
  leaveFrom: "translate-y-0",
  leaveTo: "translate-y-full",
};

const WIDTH_MAP: Record<ModalWidth, number | undefined> = {
  [ModalWidth.XS]: 238,
  [ModalWidth.S]: 400,
  [ModalWidth.M]: 512,
  [ModalWidth.L]: 672,
  [ModalWidth.XL]: 800,
  [ModalWidth.XXL]: 1152,
  [ModalWidth.XXXL]: 1600,
  [ModalWidth.FULL]: undefined,
  [ModalWidth.CUSTOM]: undefined,
};

const props = defineProps<{
  entity: T;
  title: string | ((t: T) => string);
  id?: string;
  hideTitle?: boolean;
  emphasizeTitle?: boolean;
  description?: string | ((t: T) => string);
  width: ModalWidth;
  position?: ModalPosition;
  iconMode?: IconMode;
  closeText?: string;
  customStyles?: string;
  panelStyles?: string;
  customWidthPixels?: number;
  closeIconStyles?: string;
  titleIcon?: Component;
  titleIconArgs?: object | ((t: T) => object);
  hFull?: boolean;
  backdropBlur?: boolean;
  overflowClip?: boolean;
  selected?: boolean;
  noFocus?: boolean;
  dropDisabledOverComponents?: HTMLElement[];
  dataTestid?: string;
  mobileNoSwipe?: boolean;
  mobileFullscreen?: boolean;
  noPadding?: boolean;
  hidden?: boolean;
  overflowHidden?: boolean;
  shouldUpdateKeyboardHeight?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  afterLeave: [];
  keydown: [event: KeyboardEvent];
  drop: [files: File[]];
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const parentOnAfterOpen = inject(PARENT_ON_AFTER_OPEN_FUNCTION, null);
const parentOnAfterClose = inject(PARENT_ON_AFTER_CLOSE_FUNCTION, null);

const isHidden = ref(false);

provide(PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  isHidden.value = true;
  parentOnAfterOpen?.();
});
provide(PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  isHidden.value = false;
  parentOnAfterClose?.();
});

const keyboardHeight = ref(0);

const focusRef = ref<HTMLDivElement | null>(null);
const dialogPanelRef = ref<HTMLElement | null>(null);

const colors = computed(() => colorsByTheme[pageStore.theme]);
const modalWrapperRef = ref<HTMLElement | null>(null);
const positionLeft = computed(() => props.position === ModalPosition.LEFT);
const positionRight = computed(() => props.position === ModalPosition.RIGHT);
const positionTop = computed(() => props.position === ModalPosition.TOP);
const positionBottom = computed(
  () =>
    props.position === ModalPosition.BOTTOM ||
    (pageStore.isMobile && (props.position === ModalPosition.RIGHT || props.position === undefined))
);

const widthStyles = computed(() => {
  if (props.mobileFullscreen && pageStore.isMobile) {
    return props.overflowHidden ? { height: "98vh" } : { height: "100%", maxHeight: "98vh" };
  }

  if (pageStore.isMobile && props.position !== ModalPosition.TOP && props.position !== ModalPosition.LEFT) {
    return { maxWidth: "100%" };
  }
  const width = props.customWidthPixels ?? WIDTH_MAP[props.width];
  if (width === undefined) {
    return {} as Record<string, string>;
  }
  return { maxWidth: `${width}px` };
});

const transformStyle = computed(() =>
  pageStore.isMobile && positionBottom && props.shouldUpdateKeyboardHeight && keyboardHeight.value > 0
    ? `translateY(-${keyboardHeight.value}px)`
    : undefined
);

const hasMobileSwipe = computed(() => !props.mobileNoSwipe && pageStore.isMobile);

const mobileSheetSwipe = hasMobileSwipe.value ? useMobileSheetSwipe(() => emit("close")) : undefined;

const updateKeyboardHeight = () => {
  const currentHeight = window.visualViewport?.height || window.innerHeight;
  const initialHeight = window.innerHeight;
  const heightDiff = initialHeight - currentHeight;
  keyboardHeight.value = heightDiff > 150 ? heightDiff : 0;
};
onMounted(() => {
  if (pageStore.isMobile && props.shouldUpdateKeyboardHeight) {
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateKeyboardHeight);
    } else {
      window.addEventListener("resize", updateKeyboardHeight);
    }
  }
});
onUnmounted(() => {
  if (pageStore.isMobile && props.shouldUpdateKeyboardHeight) {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", updateKeyboardHeight);
    } else {
      window.removeEventListener("resize", updateKeyboardHeight);
    }
  }
});

watch(
  () => props.entity,
  async (newEntity) => {
    if (!newEntity) {
      await when(computed(() => !modalWrapperRef.value));
      appStore.isModalOpen =
        !!document.querySelector(".dart-modal-wrapper") && !document.querySelector("#mobile-leftbar");
      return;
    }
    appStore.isModalOpen = props.id !== "mobile-leftbar";

    if (!props.noFocus) {
      return;
    }
    nextTick(() => focusRef.value?.focus());
  }
);
</script>

<template>
  <TransitionRoot appear :show="!!entity" as="template">
    <Dialog
      as="div"
      class="relative z-10 h-full transition-opacity duration-150"
      :class="{ 'opacity-0': hidden || isHidden }"
      @close="emit('close')">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <DialogOverlay
          class="fixed inset-0 bg-gray-600/40 dark:bg-zinc-500/40"
          :class="{
            'backdrop-blur-[3px]': backdropBlur,
          }" />
      </TransitionChild>

      <div
        ref="modalWrapperRef"
        class="dart-modal-wrapper fixed inset-0"
        :class="[!overflowClip && !positionRight && !pageStore.isMobile && 'overflow-y-auto']"
        :style="{
          '--background': colors.bgStd,
          '--highlight': colors.highlight,
          transform: transformStyle,
        }"
        @keydown="emit('keydown', $event)">
        <div
          class="flex h-full flex-col items-center"
          :class="[
            mobileFullscreen && pageStore.isMobile
              ? 'pt-10'
              : positionBottom
                ? pageStore.isMobile
                  ? 'justify-end px-0 pb-0'
                  : 'justify-end px-1 pb-2.5'
                : positionTop
                  ? 'justify-start px-1 pb-1 pt-2.5 sm:px-3 sm:pb-3'
                  : 'justify-evenly px-1 pb-1 pt-10 sm:p-10',
          ]">
          <TransitionChild
            as="template"
            v-bind="
              positionLeft
                ? TRANSITION_LEFT
                : positionRight
                  ? TRANSITION_RIGHT
                  : hasMobileSwipe
                    ? TRANSITION_MOBILE_SWIPE
                    : TRANSITION_NORMAL
            "
            @after-leave="emit('afterLeave')">
            <DialogPanel
              :id="id"
              ref="dialogPanelRef"
              class="w-full rounded-lg shadow-xl"
              :style="[widthStyles, panelStyles, mobileSheetSwipe?.sheetStyles?.value]"
             
              :class="{
                'absolute inset-y-2': positionLeft || positionRight,
                'left-2': positionLeft,
                '!inset-y-0 !left-0': positionLeft && noPadding,
                'right-2': positionRight,
                relative: !positionLeft && !positionRight,
                'h-full': hFull,
                'select-none': !selected,
                'transition-all': !positionLeft && !pageStore.isMobile,
              }"
              @touchstart="mobileSheetSwipe?.onTouchstart($event)"
              @touchmove="mobileSheetSwipe?.onTouchmove($event)"
              @touchend="mobileSheetSwipe?.onTouchend()">
              <div v-if="noFocus" ref="focusRef" tabindex="-1" class="sr-only" />

              <DropTarget
                v-if="$slots.dragOverlay"
                :disabled-over-components="dropDisabledOverComponents"
                styles="rounded-lg"
                class="flex flex-col"
                @drop="emit('drop', $event)">
                <template #main>
                  <ModalInner
                    :entity="entity"
                    :title="title"
                    :hide-title="hideTitle"
                    :emphasize-title="emphasizeTitle"
                    :description="description"
                    :position="position"
                    :icon-mode="iconMode"
                    :close-text="closeText"
                    :custom-styles="customStyles"
                    :close-icon-styles="closeIconStyles"
                    :title-icon="titleIcon"
                    :title-icon-args="titleIconArgs"
                    :width-style="widthStyles"
                    :has-mobile-swipe="hasMobileSwipe"
                    :overflow-hidden="overflowHidden"
                    @close="emit('close')">
                    <template #default="{ entity: slotEntity }">
                      <slot :entity="slotEntity" />
                    </template>
                    <template v-if="$slots.footerLeft" #footerLeft="{ entity: slotEntity }">
                      <slot name="footerLeft" :entity="slotEntity" />
                    </template>
                    <template v-if="$slots.actions" #actions="{ entity: slotEntity }">
                      <slot name="actions" :entity="slotEntity" />
                    </template>
                  </ModalInner>
                </template>
                <template #dragOverlay>
                  <slot name="dragOverlay" />
                </template>
              </DropTarget>
              <Template v-else>
                <ModalInner
                  :entity="entity"
                  :title="title"
                  :hide-title="hideTitle"
                  :emphasize-title="emphasizeTitle"
                  :description="description"
                  :position="position"
                  :icon-mode="iconMode"
                  :close-text="closeText"
                  :custom-styles="customStyles"
                  :close-icon-styles="closeIconStyles"
                  :title-icon="titleIcon"
                  :title-icon-args="titleIconArgs"
                  :width-style="widthStyles"
                  :has-mobile-swipe="hasMobileSwipe"
                  :overflow-hidden="overflowHidden"
                  @close="emit('close')">
                  <template #default="{ entity: slotEntity }">
                    <slot :entity="slotEntity" />
                  </template>
                  <template v-if="$slots.footerLeft" #footerLeft="{ entity: slotEntity }">
                    <slot name="footerLeft" :entity="slotEntity" />
                  </template>
                  <template v-if="$slots.actions" #actions="{ entity: slotEntity }">
                    <slot name="actions" :entity="slotEntity" />
                  </template>
                </ModalInner>
              </Template>

              <MobileNavigationSwipe v-if="pageStore.isMobile" />
            </DialogPanel>
          </TransitionChild>
          <div />
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
.dart-modal-wrapper::-webkit-scrollbar {
  background: var(--background);
}
</style>
